import { useEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { Avatar } from "./Avatar";
import { Icon } from "./Icon";
import { useCloseOnOutside } from "./FilterMenu";
import type { Owner } from "../data/types";

const TRIGGER =
  "flex h-9 w-full items-center gap-2 rounded-md border border-[var(--border-strong)] bg-[var(--bg)] px-3 text-left text-sm text-[var(--text)] shadow-xs " +
  "hover:border-gray-400 focus:border-brand-500 focus-visible:focus-ring";

/**
 * Pick a person from the roster (a request owner, an account's CE lead), each
 * shown with their avatar — a native `<select>` cannot draw one. `note` adds
 * a muted remark per member (e.g. "CE lead"). `unassignedLabel` offers "no
 * one" as the first choice, picked as the empty string.
 *
 * Keyboard: ArrowDown/Enter opens, arrows move, Enter picks, Escape closes
 * back to the trigger. While open, the enclosing dialog's `cancel` is taken
 * over, so Escape closes the list and not the dialog around it.
 */
export function OwnerPicker({
  id,
  members,
  value,
  onChange,
  note,
  unassignedLabel,
  placeholder: placeholderProp,
  disabled = false,
  ariaLabel,
}: {
  id: string;
  members: Owner[];
  value: string;
  onChange: (ownerId: string) => void;
  note?: (member: Owner) => string | undefined;
  unassignedLabel?: string;
  /** What the trigger says while nobody is chosen and there is no unassignedLabel (default "Choose an owner"). */
  placeholder?: string;
  disabled?: boolean;
  /** Names the trigger "<ariaLabel>: <current>" and the list "<ariaLabel>" (default "Owner"). */
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const current = members.find((m) => m.id === value);
  const placeholder = unassignedLabel ?? placeholderProp ?? "Choose an owner";

  useCloseOnOutside(ref, open, () => setOpen(false));

  // A modal <dialog> turns Escape into its own `cancel` (a close watcher), which
  // a keydown handler cannot stop. Claim that event while the list is open.
  useEffect(() => {
    const dialog = ref.current?.closest("dialog");
    if (!open || !dialog) return;
    const onCancel = (e: Event) => {
      e.preventDefault();
      setOpen(false);
      trigger.current?.focus();
    };
    dialog.addEventListener("cancel", onCancel);
    return () => dialog.removeEventListener("cancel", onCancel);
  }, [open]);

  const options = (): HTMLButtonElement[] => Array.from(list.current?.querySelectorAll<HTMLButtonElement>("[role=option]") ?? []);

  const openList = () => {
    setOpen(true);
    // After the list renders: focus the chosen member, or the first one.
    requestAnimationFrame(() => {
      const all = options();
      (all.find((o) => o.getAttribute("aria-selected") === "true") ?? all[0])?.focus();
    });
  };

  const close = () => {
    setOpen(false);
    trigger.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape" && open) {
      e.preventDefault();
      e.stopPropagation();
      close();
      return;
    }
    if (!open) {
      if (e.key === "ArrowDown" && e.target === trigger.current) {
        e.preventDefault();
        openList();
      }
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const all = options();
      const at = all.indexOf(document.activeElement as HTMLButtonElement);
      const next = e.key === "ArrowDown" ? Math.min(all.length - 1, at + 1) : Math.max(0, at - 1);
      all[next]?.focus();
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  return (
    <div ref={ref} className="relative" onKeyDown={onKeyDown}>
      <button
        ref={trigger}
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel ? `${ariaLabel}: ${current?.name ?? placeholder}` : undefined}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openList())}
        className={`${TRIGGER} disabled:opacity-60`}
      >
        {current ? (
          <>
            <Avatar owner={current} size="sm" />
            <span className="min-w-0 flex-1 truncate">{current.name}</span>
            {note?.(current) && <span className="flex-none text-xs text-[var(--text-2)]">{note(current)}</span>}
          </>
        ) : (
          <>
            {unassignedLabel && <EmptyAvatar />}
            <span className="flex-1 text-[var(--text-3)]">{placeholder}</span>
          </>
        )}
        <Icon name="chevrons-ud" className="flex-none text-[var(--text-3)]" />
      </button>

      {open && (
        <ul
          ref={list}
          role="listbox"
          aria-label={ariaLabel ?? "Owner"}
          className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-[var(--border)] bg-[var(--bg)] p-1 shadow-lg"
        >
          {unassignedLabel && (
            <li>
              <button
                type="button"
                role="option"
                aria-selected={value === ""}
                onClick={() => {
                  onChange("");
                  close();
                }}
                className={optionClass(value === "")}
              >
                <EmptyAvatar />
                <span className="min-w-0 flex-1 truncate">{unassignedLabel}</span>
                {value === "" && <Icon name="check" className="flex-none text-brand-600" />}
              </button>
            </li>
          )}
          {members.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                role="option"
                aria-selected={m.id === value}
                onClick={() => {
                  onChange(m.id);
                  close();
                }}
                className={optionClass(m.id === value)}
              >
                <Avatar owner={m} size="sm" />
                <span className="min-w-0 flex-1 truncate">{m.name}</span>
                {note?.(m) && <span className="flex-none text-xs text-[var(--text-2)]">{note(m)}</span>}
                {m.id === value && <Icon name="check" className="flex-none text-brand-600" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function optionClass(selected: boolean): string {
  return [
    "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-gray-100 focus:bg-gray-100 focus-visible:outline-none",
    selected ? "font-medium text-[var(--text)]" : "text-gray-700",
  ].join(" ");
}

/** The avatar slot for "no one": same size as a member's, dashed and empty. */
function EmptyAvatar() {
  return <span aria-hidden="true" className="inline-flex h-5 w-5 flex-none rounded-full border border-dashed border-[var(--border-strong)]" />;
}
